import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaBars,
  FaBell,
  FaEnvelope,
  FaMoon,
  FaSearch,
  FaSun,
  FaSyncAlt,
} from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const DEFAULT_AVATAR =
  "data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='120' height='120' rx='60' fill='%23e2e8f0'/%3E%3Ccircle cx='60' cy='46' r='22' fill='%2394a3b8'/%3E%3Cpath d='M24 106c6-24 20-36 36-36s30 12 36 36' fill='%2394a3b8'/%3E%3C/svg%3E";

const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const getMediaUrl = (url, fallback) => {
  if (!url) return fallback;
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

const getInitials = (userInfo) => {
  const first = userInfo?.prenom?.[0] || "";
  const last = userInfo?.nom?.[0] || "";
  const initials = `${first}${last}`.trim();

  return initials || "AD";
};

function BadgeButton({ to, icon: Icon, label, count = 0 }) {
  const safeCount = Number(count || 0);

  return (
    <Link
      to={to}
      title={label}
      className="relative grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:text-slate-950 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:text-white"
    >
      <Icon className="text-lg" />

      {safeCount > 0 && (
        <span className="absolute -right-1 -top-1 grid min-h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white ring-2 ring-slate-100 dark:ring-slate-950">
          {safeCount > 99 ? "99+" : safeCount}
        </span>
      )}
    </Link>
  );
}

export default function TopBarAdmin({
  toggleMenu,
  userInfo,
  title = "Tableau de bord",
  subtitle = "",
  isDarkMode,
  onToggleTheme,
  unreadCounts = {
    notifications: 0,
    messages: 0,
  },
  onRefreshBadges,
}) {
  const avatarUrl = useMemo(
    () => getMediaUrl(userInfo?.photoURL, DEFAULT_AVATAR),
    [userInfo?.photoURL]
  );

  const userFullName = useMemo(() => {
    const firstname = userInfo?.prenom || "";
    const lastname = userInfo?.nom || "";
    const fullName = `${firstname} ${lastname}`.trim();

    return fullName || "Administrateur";
  }, [userInfo?.nom, userInfo?.prenom]);

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200/80 bg-slate-100/85 backdrop-blur-xl transition-colors dark:border-slate-800/80 dark:bg-slate-950/85">
      <div className="mx-auto flex min-h-[88px] w-full max-w-[1600px] items-center gap-4 px-4 py-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={toggleMenu}
          aria-label="Ouvrir le menu"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-slate-700 lg:hidden"
        >
          <FaBars />
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-200">
              Administration
            </span>

            <span className="hidden rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 sm:inline-flex">
              MonAppliClub Light
            </span>
          </div>

          <h1 className="mt-2 truncate text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
            {title}
          </h1>

          {subtitle && (
            <p className="mt-1 line-clamp-1 text-sm text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          )}
        </div>

        <div className="hidden min-w-[260px] max-w-sm flex-1 items-center rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 xl:flex">
          <FaSearch className="mr-3 text-slate-400" />
          <input
            type="search"
            placeholder="Rechercher dans l’administration..."
            className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-100"
          />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onRefreshBadges}
            title="Actualiser les compteurs"
            className="hidden h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:text-slate-950 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:text-white sm:grid"
          >
            <FaSyncAlt className="text-sm" />
          </button>

          <button
            type="button"
            onClick={onToggleTheme}
            title={isDarkMode ? "Activer le mode clair" : "Activer le mode sombre"}
            className="grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:text-slate-950 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:text-white"
          >
            {isDarkMode ? (
              <FaSun className="text-lg text-yellow-500" />
            ) : (
              <FaMoon className="text-lg" />
            )}
          </button>

          {/* <BadgeButton
            to="/appli/notifications"
            icon={FaBell}
            label="Notifications"
            count={unreadCounts.notifications}
          />

          <BadgeButton
            to="/appli/messages-contact"
            icon={FaEnvelope}
            label="Messages du formulaire de contact"
            count={unreadCounts.messages}
          /> */}

          <div className="hidden items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:flex">
            <div className="relative">
              <img
                src={avatarUrl}
                alt={userFullName}
                className="h-10 w-10 rounded-xl object-cover"
              />
              <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500 dark:border-slate-900" />
            </div>

            <div className="min-w-0">
              <p className="max-w-[150px] truncate text-sm font-bold text-slate-900 dark:text-white">
                {userFullName}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {getInitials(userInfo)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}